/**
 * StripeConnectInfoModal Component
 * Explains what Stripe Connect Express onboarding will collect and why,
 * shown before the landlord's first "Start Verification" click.
 * Static copy only — no data fetching.
 */

import Modal from './Modal';

interface StripeConnectInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  isLoading: boolean;
}

interface InfoSection {
  id: string;
  label: string;
  what: string;
  why: string;
}

const SECTIONS: InfoSection[] = [
  {
    id: 'identity',
    label: 'Identity',
    what: 'Legal name, date of birth, and SSN or ITIN',
    why: 'Required by federal law to verify who is receiving the money, and helps prevent fraudulent listings from being posted under a fake identity.',
  },
  {
    id: 'business',
    label: "If you're connecting as a business",
    what: 'EIN, legal business name and address',
    why: 'Only requested if you choose to connect as a business rather than an individual.',
  },
  {
    id: 'contact',
    label: 'Contact',
    what: 'Address and phone number',
    why: 'So Stripe can verify your identity and reach you about your account.',
  },
  {
    id: 'banking',
    label: 'Banking',
    what: 'Bank routing and account number',
    why: 'So rent payments can be deposited directly to your account, and so we can generate your 1099-K tax form each year.',
  },
];

export function StripeConnectInfoModal({
  isOpen,
  onClose,
  onContinue,
  isLoading,
}: StripeConnectInfoModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Before you connect your bank account"
      data-testid="stripe-connect-info-modal"
    >
      <div className="max-h-[60vh] overflow-y-auto px-1">
        <p className="text-sm text-muted-foreground mt-0 mb-4">
          Our payment partner, Stripe, will ask you for the following information to verify
          your identity and set up payouts.
        </p>
        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.id} data-testid={`stripe-connect-info-${section.id}`}>
              <h3 className="text-sm font-medium text-foreground m-0">{section.label}</h3>
              <p className="text-sm text-foreground mt-1 mb-0.5">{section.what}</p>
              <p className="text-sm text-muted-foreground mt-0 m-0">{section.why}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={onClose}
          disabled={isLoading}
          className="px-5 py-2.5 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md cursor-pointer hover:bg-accent min-h-[44px] disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="stripe-connect-info-cancel-button"
        >
          Cancel
        </button>
        <button
          onClick={onContinue}
          disabled={isLoading}
          className="px-5 py-2.5 text-sm font-medium text-primary-foreground bg-primary border-none rounded-md cursor-pointer hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
          data-testid="stripe-connect-info-continue-button"
        >
          {isLoading ? 'Setting up...' : 'Continue to Stripe'}
        </button>
      </div>
    </Modal>
  );
}

export default StripeConnectInfoModal;
